// filepath: apps/web/src/features/admin/templates/update/queries.ts
//
// FR-UI-ADM-12 AC-1..AC-8 — TanStack Query hooks for the
// template-update flow.
//
// INV-1 (cross-tenant): the cache key includes `tenantSlug` as
// the 2nd segment. Switching tenants invalidates the prior
// tenant's cache and refetches the new tenant's view.
//
// FR-NFR-3 / ARCH Rule 3 (idempotency): the apply mutation carries
// `idempotency_key: crypto.randomUUID()`. The server checks
// `platform.idempotency_keys WHERE tenant_id = $1` BEFORE any
// work; replays return the cached response.
//
// INV-7 (optimistic concurrency): the apply mutation sends
// `If-Match: <installed_version>`. The server compares the
// header against the currently installed version and returns
// 409 INSTALLED_VERSION_DRIFT on mismatch.
//
// 5-state fetch contract (FR-UI-CORE-10): the query hook handles
// the loading / fetch-failure / permission-denied / rate-limit /
// staging-only states. The mutation hook additionally handles
// the validation-failed / unresolved-conflicts /
// installed-version-drift / not-installed / idempotency-replay /
// invalid-resolution states.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type {
  ApplyUpdateRequest,
  ApplyUpdateResponse,
  TemplateUpdateFetchError,
  TemplateUpdateListResponse,
  TemplateUpdateOffer,
} from "./types";

/**
 * Cache keys for the template-update flow. tenantSlug is the
 * 2nd segment (INV-1). The list key shape mirrors the
 * FR-UI-ADM-11 registry key shape (`["admin", tenantSlug, ...]`).
 */
export const templateUpdateKeys = {
  list: (tenantSlug: string) =>
    ["admin", tenantSlug, "template-updates"] as const,
  offer: (
    tenantSlug: string,
    templateId: string,
    installed: string,
    target: string,
  ) =>
    [
      "admin",
      tenantSlug,
      "template-update-offer",
      templateId,
      installed,
      target,
    ] as const,
};

/**
 * useTemplateUpdates — GET /api/templates/installed/updates
 * (AC-1). Lists per-tenant installed templates with newer
 * platform-published versions. Drives the notification badge.
 */
export function useTemplateUpdates() {
  const { tenantSlug } = useTenant();
  return useQuery<TemplateUpdateListResponse, ApiError>({
    queryKey: templateUpdateKeys.list(tenantSlug),
    queryFn: async () => {
      const r = await apiFetch("/api/templates/installed/updates");
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as TemplateUpdateListResponse;
    },
  });
}

/**
 * useTemplateUpdateOffer — GET /api/templates/{id}/update-offer
 * (AC-2 / AC-3). Returns the structured ConflictReport for the
 * (installed, target) tuple. The query is enabled only when
 * templateId, installed, and target are all non-empty.
 */
export function useTemplateUpdateOffer(
  templateId: string | null,
  installed: string | null,
  target: string | null,
) {
  const { tenantSlug } = useTenant();
  return useQuery<TemplateUpdateOffer, ApiError>({
    queryKey:
      templateId && installed && target
        ? templateUpdateKeys.offer(tenantSlug, templateId, installed, target)
        : (["admin", tenantSlug, "template-update-offer", "disabled"] as const),
    queryFn: async () => {
      if (!templateId || !installed || !target) {
        throw new ApiError("missing parameters", 400, "missing_params");
      }
      const params = new URLSearchParams({ installed, target });
      const r = await apiFetch(
        `/api/templates/${encodeURIComponent(templateId)}/update-offer?${params.toString()}`,
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as TemplateUpdateOffer;
    },
    enabled: Boolean(templateId && installed && target),
  });
}

/**
 * useTemplateUpdateApply — POST /api/templates/{id}/apply-update
 * (AC-4..AC-8). Sends X-Idempotency-Key, If-Match, and the
 * resolved bundle. Maps every error kind to the typed union.
 */
export function useTemplateUpdateApply(templateId: string) {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<
    ApplyUpdateResponse,
    Error | TemplateUpdateFetchError,
    { request: ApplyUpdateRequest; ifMatch: string }
  >({
    mutationFn: async ({ request, ifMatch }) => {
      const r = await apiFetch(
        `/api/templates/${encodeURIComponent(templateId)}/apply-update`,
        {
          method: "POST",
          init: {
            headers: {
              "Content-Type": "application/json",
              "If-Match": ifMatch,
            },
            body: JSON.stringify(request),
          },
          idempotencyKey: true,
        },
      );
      if (r.status === 200 || r.status === 202) {
        return (await r.json()) as ApplyUpdateResponse;
      }
      const body = await r
        .json()
        .catch(() => ({}) as { error?: string; code?: string });
      throw classifyApplyError(r.status, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: templateUpdateKeys.list(tenantSlug),
      });
      // BA contract (uat-ba note_13124): disarm after success.
    },
    onError: () => {
      // BA contract (uat-ba note_13124): disarm on failure.
    },
    onSettled: () => {
      // Belt-and-braces; both branches above already disarm.
    },
  });
}

/**
 * classifyApplyError — maps an HTTP status + body to the typed
 * TemplateUpdateFetchError union. Used by the apply mutation.
 */
function classifyApplyError(
  status: number,
  body: {
    error?: string;
    code?: string;
    errors?: Array<{ node_id: string; type: string; message: string }>;
  },
): TemplateUpdateFetchError {
  const code = body.code ?? "";
  const message = body.error ?? "unknown error";
  if (status === 403 && code === "ENV_PRODUCTION") {
    return { kind: "staging_only", message, retriable: false };
  }
  if (status === 403) {
    return { kind: "permission_denied", message, retriable: false };
  }
  if (status === 404 && code === "NOT_INSTALLED") {
    return { kind: "not_installed", message, retriable: false };
  }
  if (status === 409 && code === "INSTALLED_VERSION_DRIFT") {
    return { kind: "installed_version_drift", message, retriable: true };
  }
  if (status === 409 && code === "UNRESOLVED_CONFLICTS") {
    return { kind: "unresolved_conflicts", message, retriable: true };
  }
  if (status === 422 && code === "VALIDATION_FAILED") {
    return {
      kind: "validation_failed",
      message,
      errors: body.errors ?? [],
      retriable: true,
    };
  }
  if (status === 400 && code === "INVALID_RESOLUTION") {
    return { kind: "invalid_resolution", message, retriable: true };
  }
  if (status === 429) {
    return { kind: "rate_limit", message, retriable: true };
  }
  return { kind: "fetch_failure", message, retriable: status >= 500 };
}

/**
 * classifyTemplateUpdateFetchError — maps a transport-level
 * ApiError to the typed TemplateUpdateFetchError union. Used by
 * the list / offer query hooks.
 */
export function classifyTemplateUpdateFetchError(
  err: ApiError,
): TemplateUpdateFetchError {
  switch (err.status) {
    case 403:
      return {
        kind: "permission_denied",
        message: err.message || "permission denied",
        retriable: false,
      };
    case 429:
      return {
        kind: "rate_limit",
        message: err.message || "rate limited",
        retriable: true,
      };
    case 0:
      return {
        kind: "fetch_failure",
        message: err.message || "network error",
        retriable: true,
      };
    default:
      return {
        kind: "fetch_failure",
        message: err.message || err.code || "fetch failure",
        retriable: err.status >= 500,
      };
  }
}
